/**
 * Common UI translations shared across the dashboard
 */
export const common = {
  en: {
    loading: "Loading...",
    error: "Error",
    success: "Success",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    search: "Search",
    filter: "Filter",
    noResults: "No results found",
    viewAll: "View all",
    back: "Back",
    next: "Next",
    previous: "Previous",
    submit: "Submit",
    close: "Close",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    actions: "Actions",
    status: "Status",
    date: "Date",
    name: "Name",
    description: "Description",
    type: "Type",
    view: "View",
    retry: "Retry",
    deleting: "Deleting...",
    creating: "Creating...",
    saving: "Saving...",
    updating: "Updating...",
    today: "Today",
    yesterday: "Yesterday",
    daysAgo: "days ago",
    complete: "complete",
    continue: "Continue",
    deleteDraft: "Delete draft",
    deleteCannotUndo: "This action cannot be undone.",
    permanentlyDeleted: "will be permanently deleted.",
    noDescription: "No description",
    active: "Active",
    inactive: "Inactive",
    items: "items",
    more: "more",
    // Upload shared strings
    clickToUpload: "Click to upload",
    orDragAndDrop: "or drag and drop",
    uploadFailed: "Upload failed",
    uploading: "Uploading...",
    replace: "Replace",
    remove: "Remove",
    // ErrorBoundary
    somethingWentWrong: "Something went wrong",
    unexpectedError: "An unexpected error occurred while rendering this component.",
    tryAgain: "Try Again",
    // Shared form labels
    light: "Light",
    dark: "Dark",
    preview: "Preview",
    // Additional shared
    of: "of",
    showing: "Showing",
    requests: "requests",
    newRequest: "New Request",
    // Additional shared UI strings
    processing: "Processing...",
    add: "Add",
    send: "Send",
    internalOnly: "Internal only (hidden from customer)",
    agencyView: "Agency View",
    clientView: "Client View",
    navigationMenu: "Navigation Menu",
    aiIsTyping: "AI is typing",
    noTasks: "No tasks",
    dragTasksHere: "Drag tasks here",
    noRequests: "No requests",
    dragRequestsHere: "Drag requests here",
    noTickets: "No tickets",
    dragTicketsHere: "Drag tickets here",
    noIncomingItems: "No incoming items",
    newRequestsWillAppear: "New requests and tasks will appear here",
    justNow: "Just now",
    minuteAgo: "minute ago",
    minutesAgo: "minutes ago",
    hourAgo: "hour ago",
    hoursAgo: "hours ago",
    // Relative time short
    minutesAgoShort: "m ago",
    hoursAgoShort: "h ago",
    daysAgoShort: "d ago",
    // Statuses
    notStarted: "Not Started",
    inProgress: "In Progress",
    blocked: "Blocked",
    completed: "Completed",
    pending: "Pending",
    done: "Done",
    // Priorities
    low: "Low",
    medium: "Medium",
    high: "High",
    urgent: "Urgent",
    // Execution phases
    phaseRequest: "Request",
    phaseBacklog: "Backlog",
    phaseBlocked: "Blocked",
    phaseTodo: "To-Do",
    phaseInProgress: "In Progress",
    phaseReview: "Review",
    phaseDone: "Done",
    phaseArchived: "Archived",
    phaseRequestDesc: "New service requests awaiting review",
    phaseBacklogDesc: "Approved work waiting to be scheduled",
    phaseBlockedDesc: "Work that cannot proceed due to dependencies",
    phaseTodoDesc: "Ready to start, scheduled for current cycle",
    phaseInProgressDesc: "Actively being worked on",
    phaseReviewDesc: "Completed work awaiting approval",
    phaseDoneDesc: "Completed and delivered",
    phaseArchivedDesc: "Completed and stored for reference",
    // Customer phase labels
    yourRequests: "Your Requests",
    scheduled: "Scheduled",
    onHold: "On Hold",
    upcoming: "Upcoming",
    delivered: "Delivered",
    // Editor toolbar
    boldShortcut: "Bold (Ctrl+B)",
    italicShortcut: "Italic (Ctrl+I)",
    underlineShortcut: "Underline (Ctrl+U)",
    strikethrough: "Strikethrough",
    bulletList: "Bullet List",
    numberedList: "Numbered List",
    quoteLabel: "Quote",
    codeLabel: "Code",
    addLink: "Add Link",
    uploadImage: "Upload Image",
    // Task card / shared approvals
    createdBy: "Created by",
    assigneeLabel: "Assignee:",
    blockerCount: "blocker(s)",
    clientApproved: "Client Approved",
    clientRejected: "Client Rejected",
    pendingClient: "Pending Client",
    clientApprovalNeeded: "Client Approval Needed",
    teamApproved: "Team Approved",
    teamRejected: "Team Rejected",
    pendingTeam: "Pending Team",
    teamApprovalNeeded: "Team Approval Needed",
    yourApprovalNeeded: "Your approval needed:",
    reject: "Reject",
    approve: "Approve",
    archive: "Archive",
    // Accessibility
    skipToContent: "Skip to main content",
    // Card editor
    cardLabel: "Card",
    // Chat
    typeYourMessage: "Type your message...",
    // Service request card
    viewDetailsAction: "View Details",
    convertToProject: "Convert to Project",
    deleteRequestAction: "Delete Request",
    // Service request page aria-labels
    searchServiceRequests: "Search service requests",
    filterByStatus: "Filter by status",
    filterByServiceType: "Filter by service type",
    tableView: "Table view",
    kanbanView: "Kanban view",
    // Error messages
    failedToFetchServiceTypes: "Failed to fetch service types",
    failedToCreateRequest: "Failed to create request",
    unexpectedErrorOccurred: "An unexpected error occurred",
    onlyDraftCanBeEdited: "Only draft requests can be edited",
    failedToLoadRequest: "Failed to load request",
    deleteServiceRequest: "Delete service request",
    // Social media
    postsCount: "posts",
  },
  es: {
    loading: "Cargando...",
    error: "Error",
    success: "Éxito",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    create: "Crear",
    search: "Buscar",
    filter: "Filtrar",
    noResults: "No se encontraron resultados",
    viewAll: "Ver todo",
    back: "Volver",
    next: "Siguiente",
    previous: "Anterior",
    submit: "Enviar",
    close: "Cerrar",
    confirm: "Confirmar",
    yes: "Sí",
    no: "No",
    actions: "Acciones",
    status: "Estado",
    date: "Fecha",
    name: "Nombre",
    description: "Descripción",
    type: "Tipo",
    view: "Ver",
    retry: "Reintentar",
    deleting: "Eliminando...",
    creating: "Creando...",
    saving: "Guardando...",
    updating: "Actualizando...",
    today: "Hoy",
    yesterday: "Ayer",
    daysAgo: "días atrás",
    complete: "completo",
    continue: "Continuar",
    deleteDraft: "Eliminar borrador",
    deleteCannotUndo: "Esta acción no se puede deshacer.",
    permanentlyDeleted: "será eliminado permanentemente.",
    noDescription: "Sin descripción",
    active: "Activos",
    inactive: "Inactivo",
    items: "elementos",
    more: "más",
    // Upload shared strings
    clickToUpload: "Clic para cargar",
    orDragAndDrop: "o arrastra y suelta",
    uploadFailed: "Error al cargar",
    uploading: "Cargando...",
    replace: "Reemplazar",
    remove: "Eliminar",
    // ErrorBoundary
    somethingWentWrong: "Algo salió mal",
    unexpectedError: "Ocurrió un error inesperado al renderizar este componente.",
    tryAgain: "Reintentar",
    // Shared form labels
    light: "Claro",
    dark: "Oscuro",
    preview: "Vista Previa",
    // Additional shared
    of: "de",
    showing: "Mostrando",
    requests: "solicitudes",
    newRequest: "Nueva Solicitud",
    // Additional shared UI strings
    processing: "Procesando...",
    add: "Agregar",
    send: "Enviar",
    internalOnly: "Solo interno (oculto del cliente)",
    agencyView: "Vista Agencia",
    clientView: "Vista Cliente",
    navigationMenu: "Menú de Navegación",
    aiIsTyping: "IA está escribiendo",
    noTasks: "Sin tareas",
    dragTasksHere: "Arrastra tareas aquí",
    noRequests: "Sin solicitudes",
    dragRequestsHere: "Arrastra solicitudes aquí",
    noTickets: "Sin tickets",
    dragTicketsHere: "Arrastra tickets aquí",
    noIncomingItems: "Sin elementos entrantes",
    newRequestsWillAppear: "Las nuevas solicitudes y tareas aparecerán aquí",
    justNow: "Ahora mismo",
    minuteAgo: "minuto atrás",
    minutesAgo: "minutos atrás",
    hourAgo: "hora atrás",
    hoursAgo: "horas atrás",
    // Relative time short
    minutesAgoShort: "min",
    hoursAgoShort: "h",
    daysAgoShort: "d",
    // Statuses
    notStarted: "Sin Iniciar",
    inProgress: "En Progreso",
    blocked: "Bloqueado",
    completed: "Completado",
    pending: "Pendiente",
    done: "Hecho",
    // Priorities
    low: "Baja",
    medium: "Media",
    high: "Alta",
    urgent: "Urgente",
    // Execution phases
    phaseRequest: "Solicitud",
    phaseBacklog: "Pendientes",
    phaseBlocked: "Bloqueado",
    phaseTodo: "Por Hacer",
    phaseInProgress: "En Progreso",
    phaseReview: "Revisión",
    phaseDone: "Hecho",
    phaseArchived: "Archivado",
    phaseRequestDesc: "Nuevas solicitudes de servicio en espera de revisión",
    phaseBacklogDesc: "Trabajo aprobado en espera de programación",
    phaseBlockedDesc: "Trabajo que no puede continuar debido a dependencias",
    phaseTodoDesc: "Listo para comenzar, programado para el ciclo actual",
    phaseInProgressDesc: "Actualmente en desarrollo",
    phaseReviewDesc: "Trabajo completado en espera de aprobación",
    phaseDoneDesc: "Completado y entregado",
    phaseArchivedDesc: "Completado y almacenado como referencia",
    // Customer phase labels
    yourRequests: "Tus Solicitudes",
    scheduled: "Programado",
    onHold: "En Espera",
    upcoming: "Próximo",
    delivered: "Entregado",
    // Editor toolbar
    boldShortcut: "Negrita (Ctrl+B)",
    italicShortcut: "Cursiva (Ctrl+I)",
    underlineShortcut: "Subrayado (Ctrl+U)",
    strikethrough: "Tachado",
    bulletList: "Lista con Viñetas",
    numberedList: "Lista Numerada",
    quoteLabel: "Cita",
    codeLabel: "Código",
    addLink: "Agregar Enlace",
    uploadImage: "Subir Imagen",
    // Task card / shared approvals
    createdBy: "Creado por",
    assigneeLabel: "Asignado:",
    blockerCount: "bloqueador(es)",
    clientApproved: "Cliente Aprobó",
    clientRejected: "Cliente Rechazó",
    pendingClient: "Cliente Pendiente",
    clientApprovalNeeded: "Requiere Aprobación del Cliente",
    teamApproved: "Equipo Aprobó",
    teamRejected: "Equipo Rechazó",
    pendingTeam: "Equipo Pendiente",
    teamApprovalNeeded: "Requiere Aprobación del Equipo",
    yourApprovalNeeded: "Tu aprobación es requerida:",
    reject: "Rechazar",
    approve: "Aprobar",
    archive: "Archivar",
    // Accessibility
    skipToContent: "Saltar al contenido principal",
    // Card editor
    cardLabel: "Tarjeta",
    // Chat
    typeYourMessage: "Escribe tu mensaje...",
    // Service request card
    viewDetailsAction: "Ver Detalles",
    convertToProject: "Convertir a Proyecto",
    deleteRequestAction: "Eliminar Solicitud",
    // Service request page aria-labels
    searchServiceRequests: "Buscar solicitudes de servicio",
    filterByStatus: "Filtrar por estado",
    filterByServiceType: "Filtrar por tipo de servicio",
    tableView: "Vista de tabla",
    kanbanView: "Vista kanban",
    // Error messages
    failedToFetchServiceTypes: "Error al obtener tipos de servicio",
    failedToCreateRequest: "Error al crear solicitud",
    unexpectedErrorOccurred: "Ocurrió un error inesperado",
    onlyDraftCanBeEdited: "Solo las solicitudes en borrador pueden editarse",
    failedToLoadRequest: "Error al cargar solicitud",
    deleteServiceRequest: "Eliminar solicitud de servicio",
    // Social media
    postsCount: "publicaciones",
  },
};
